/**
 * Mini-formulaire d'édition des informations chantier d'une simulation DOS-DOMIUM.
 * Permet de compléter ou rectifier les champs Article 2 (CP) après la simulation
 * initiale (ex : permis arrivé tardivement, dates affinées). Les valeurs sont
 * persistées en MongoDB et exploitées par le générateur PDF du Dossier Maître.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Label } from '../ui/label';
import { Loader2, Save, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EditChantierInfoDialog({ open, onOpenChange, simulation, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    type_construction: '',
    type_travaux: '',
    destination_operation: '',
    adresse_chantier: '',
    reference_permis: '',
    reference_cadastrale: '',
    date_ouverture_chantier: '',
    date_reception_previsionnelle: '',
  });

  React.useEffect(() => {
    if (!open || !simulation?.reference) return;
    let cancelled = false;
    setLoading(true);
    const token = localStorage.getItem('token');
    axios
      .get(`${BACKEND_URL}/api/domium/devis/${encodeURIComponent(simulation.reference)}/chantier-info`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};
        setForm({
          type_construction: d.type_construction || '',
          type_travaux: d.type_travaux || '',
          destination_operation: d.destination_operation || '',
          adresse_chantier: d.adresse_chantier || '',
          reference_permis: d.reference_permis || '',
          reference_cadastrale: d.reference_cadastrale || '',
          date_ouverture_chantier: d.date_ouverture_chantier || '',
          date_reception_previsionnelle: d.date_reception_previsionnelle || '',
        });
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Impossible de charger les informations chantier');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, simulation?.reference]);

  const handleField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    if (!simulation?.reference) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== '' && v != null)
      );
      const res = await axios.patch(
        `${BACKEND_URL}/api/domium/devis/${encodeURIComponent(simulation.reference)}/chantier-info`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(res.data?.message || 'Informations chantier mises à jour');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-slate-200 shadow-xl" data-testid="edit-chantier-info-dialog">
        <DialogHeader className="border-b border-slate-100 pb-4">
          <DialogTitle className="flex items-center gap-2.5 text-slate-900">
            <span className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-indigo-50 text-indigo-600">
              <Pencil className="h-4 w-4" />
            </span>
            <span className="font-semibold tracking-tight">Compléter les informations chantier</span>
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-sm leading-relaxed mt-1.5">
            Renseignez les éléments arrivés après la simulation (permis, dates, type de travaux…).
            Ces valeurs seront automatiquement reprises dans les Conditions Particulières (page 2)
            du Dossier Maître DOS-DOMIUM.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Chargement…
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="type_construction">Type de construction</Label>
              <Input id="type_construction" placeholder="Ex : Maison individuelle isolée" value={form.type_construction} onChange={handleField('type_construction')} data-testid="input-type-construction" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="type_travaux">Type de travaux</Label>
              <Input id="type_travaux" placeholder="Ex : Construction neuve avec terrassement" value={form.type_travaux} onChange={handleField('type_travaux')} data-testid="input-type-travaux" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="destination_operation">Destination</Label>
              <Input id="destination_operation" placeholder="Ex : Habitation principale" value={form.destination_operation} onChange={handleField('destination_operation')} data-testid="input-destination" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="adresse_chantier">Adresse de l&apos;opération</Label>
              <Input id="adresse_chantier" placeholder="Ex : 200 Allée Victor Schoelcher, 97120 Saint-Claude" value={form.adresse_chantier} onChange={handleField('adresse_chantier')} data-testid="input-adresse-chantier" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reference_permis">Permis de construire</Label>
              <Input id="reference_permis" placeholder="Ex : PC971242600008" value={form.reference_permis} onChange={handleField('reference_permis')} data-testid="input-permis" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reference_cadastrale">Référence cadastrale</Label>
              <Input id="reference_cadastrale" placeholder="Ex : AR 271 et AR 225" value={form.reference_cadastrale} onChange={handleField('reference_cadastrale')} data-testid="input-cadastre" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="date_ouverture_chantier">Date d&apos;ouverture de chantier</Label>
              <DatePickerFr id="date_ouverture_chantier" className="w-full" value={form.date_ouverture_chantier} onChange={(v) => handleField('date_ouverture_chantier')({ target: { value: v } })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="date_reception_previsionnelle">Date de réception prévisionnelle</Label>
              <DatePickerFr id="date_reception_previsionnelle" className="w-full" value={form.date_reception_previsionnelle} onChange={(v) => handleField('date_reception_previsionnelle')({ target: { value: v } })} />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="btn-cancel-chantier-info">
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={loading || saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="btn-save-chantier-info">
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enregistrement…</>
            ) : (
              <><Save className="h-4 w-4 mr-2" />Enregistrer</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
